import React, { useState, useEffect, useRef } from 'react';
import { useAuthenticator, Authenticator } from '@aws-amplify/ui-react';
import { fetchOrUpdateUserStatus } from './Membership';
import { Container } from 'react-bootstrap';
import { BuyButton } from './BuyButtons';
import { SignUpPrompt } from './SignUpPrompt';
import SiteCard from './SiteCard';

/**
 * Higher-order component that requires an active subscription to access content
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content to render if subscribed
 * @param {string} props.featureName - Name of the feature being gated (for messaging)
 */
export function RequireSubscription({ children, featureName = "this feature" }) {
    const { user } = useAuthenticator();
    const [isSubscribed, setIsSubscribed] = useState(null); // null = loading
    const [userEmail, setUserEmail] = useState(null);
    const lastUserIdRef = useRef(null);

    useEffect(() => {
        const checkSubscription = async () => {
            if (user && lastUserIdRef.current !== user.userId) {
                try {
                    const result = await fetchOrUpdateUserStatus(user);
                    const isActive = result?.stripe?.stripe_sub_status === 'active';
                    setIsSubscribed(isActive);

                    if (result?.email) {
                        setUserEmail(result.email);
                    }
                    lastUserIdRef.current = user.userId;
                } catch (error) {
                    console.error("Error checking subscription:", error);
                    setIsSubscribed(false);
                }
            } else if (!user) {
                setIsSubscribed(false);
            }
        };
        checkSubscription();
    }, [user]);

    // Loading state
    if (isSubscribed === null) {
        return (
            <Container className="mt-5 text-center">
                <p>Loading...</p>
            </Container>
        );
    }

    // Not subscribed or not logged in - show appropriate prompt
    if (!isSubscribed) {
        // User is not logged in - prompt to sign up
        if (!user) {
            return (
                <Container className="mt-5">
                    <SiteCard
                        header="ACCOUNT REQUIRED"
                        title={`Sign up to access ${featureName}`}
                        content={
                            <SignUpPrompt
                                message="Create a free account to get started. Premium features require a subscription."
                            />
                        }
                    />
                </Container>
            );
        }

        // User is logged in but not subscribed - show subscription paywall
        return (
            <Container className="mt-5">
                <SiteCard
                    header="SUBSCRIPTION REQUIRED"
                    title={`Subscribe to access ${featureName}`}
                    content={
                        <div style={{ textAlign: 'center' }}>
                            <p>This feature is available to subscribers only.</p>
                            <p>Subscribe now for unlimited access to all premium features.</p>
                            <BuyButton userEmail={userEmail} />
                        </div>
                    }
                />
            </Container>
        );
    }

    // Subscribed - render children
    return <>{children}</>;
}

/**
 * Hook to check subscription status - safe to use without Authenticator.Provider
 * @returns {{isSubscribed: boolean|null, isLoading: boolean, userEmail: string|null, isAuthenticated: boolean}}
 */
export function useSubscription() {
    const [isSubscribed, setIsSubscribed] = useState(null);
    const [userEmail, setUserEmail] = useState(null);
    const lastUserIdRef = useRef(null);

    // Try to get user, but handle case where Authenticator.Provider isn't available
    let user = null;
    try {
        const auth = useAuthenticator((context) => [context.user]);
        user = auth.user;
    } catch (error) {
        // Not wrapped in Authenticator.Provider - treat as not logged in
        user = null;
    }

    useEffect(() => {
        const checkSubscription = async () => {
            if (user && lastUserIdRef.current !== user.userId) {
                try {
                    const result = await fetchOrUpdateUserStatus(user);
                    const isActive = result?.stripe?.stripe_sub_status === 'active';
                    setIsSubscribed(isActive);

                    if (result?.email) {
                        setUserEmail(result.email);
                    }
                    lastUserIdRef.current = user.userId;
                } catch (error) {
                    console.error("Error checking subscription:", error);
                    setIsSubscribed(false);
                }
            } else if (!user) {
                setIsSubscribed(false);
            }
        };
        checkSubscription();
    }, [user]);

    return {
        isSubscribed,
        isLoading: isSubscribed === null,
        userEmail,
        isAuthenticated: !!user
    };
}

/**
 * Wrapper component that provides Authenticator context if needed
 * Use this to wrap pages that need subscription checks but aren't already in Authenticator.Provider
 */
export function WithAuthenticator({ children }) {
    return (
        <Authenticator.Provider>
            {children}
        </Authenticator.Provider>
    );
}
